import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { I18nProvider } from './contexts/I18nContext';
import { Toaster } from './components/ui/sonner';
import CommandPalette from './components/CommandPalette';
import ReviewDuePopup from './components/ReviewDuePopup';
import ReviewPromptDialog from './components/ReviewPromptDialog';
import { FloatingStudyTimer } from './components/FloatingStudyTimer';
import HalloweenDecorations from './components/HalloweenDecorations';

// Pages
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import ImportText from './pages/ImportText';
import Reader from './pages/Reader';
import Vocabulary from './pages/Vocabulary';
import Review from './pages/Review';
import MemoryDashboard from './pages/MemoryDashboard';
import Stats from './pages/Stats';
import Settings from './pages/Settings';
import GoalSetter from './pages/GoalSetter';
import Onboarding from './pages/Onboarding';
import AdminDashboard from './pages/AdminDashboard';
import AdminEntries from './pages/AdminEntries';
import AdminUsers from './pages/AdminUsers';
import AdminTexts from './pages/AdminTexts';
import AdminFrequencies from './pages/AdminFrequencies';
import AdminSentences from './pages/AdminSentences';
import AdminFeaturedVideos from './pages/AdminFeaturedVideos';
import AdminGrammarEditor from './pages/AdminGrammarEditor';
import AdminEntryAudit from './pages/AdminEntryAudit';
import AdminFlashcards from './pages/AdminFlashcards';
import AdminFlashcardsStudy from './pages/AdminFlashcardsStudy';
import SentenceExplorer from './pages/SentenceExplorer';
import BatchUpload from './pages/BatchUpload';
import TextsList from './pages/TextsList';
import TextViewer from './pages/TextViewer';
import Game from './pages/Game';
import GamesHub from './pages/GamesHub';
import MailRoom from './pages/MailRoom';
import BibleCourses from './pages/BibleCourses';
import BibleBook from './pages/BibleBook';
import BibleReader from './pages/BibleReader';
import GrammarTopics from './pages/GrammarTopics';
import GrammarLibrary from './pages/GrammarLibrary';
import LearningPath from './pages/LearningPath';
import AlphabetCourses from './pages/AlphabetCourses';
import AlphabetLesson from './pages/AlphabetLesson';
import Community from './pages/Community';
import ForumCategory from './pages/ForumCategory';
import ForumPost from './pages/ForumPost';
import ForumNewPost from './pages/ForumNewPost';
import ChatRoom from './pages/ChatRoom';
import ChatRoomRequest from './pages/ChatRoomRequest';
import ForgePath from './pages/forge/ForgePath';
import ForgeLesson from './pages/forge/ForgeLesson';
import ForgeLanguageSelection from './pages/forge/ForgeLanguageSelection';
import ForgeStories from './pages/forge/ForgeStories';
import ForgeStoryReader from './pages/forge/ForgeStoryReader';
import ForgeStoriesIndex from './pages/forge/ForgeStoriesIndex';
import ListeningPage from './pages/forge/ListeningPage';
import ConversationPage from './pages/ConversationPage';
import BuyCredits from './pages/BuyCredits';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import Library from './pages/Library';
import SeriesList from './pages/SeriesList';
import SeriesDetail from './pages/SeriesDetail';
import VideoLibrary from './pages/VideoLibrary';
import VideoReader from './pages/VideoReader';
import AdminLogs from './pages/AdminLogs';
import AdminLanguageRequests from './pages/AdminLanguageRequests';
import Vault from './pages/Vault';
import SentenceLibraryPage from './pages/SentenceLibraryPage';
import LanguagePriorityAssessment from './pages/LanguagePriorityAssessment';
import VerbFormationLibrary from './pages/VerbFormationLibrary';
import OracleEmbed from './pages/OracleEmbed';

// Components
import Layout from './components/Layout';
import logger from './utils/logger';

import './index.css';

// Protected Route wrapper
function ProtectedRoute({ children, showLayout = true, allowOnboarding = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Boot the frontend logger when the user is known
  if (user) logger.init(user.user_id || user.id || null);

  // Track page for log context
  logger.setPage(location.pathname);

  // If user data was passed from AuthCallback, use it immediately
  if (location.state?.user) {
    return showLayout ? <Layout>{children}</Layout> : children;
  }

  // Block only while loading AND we have no user at all (no cache either).
  // If we have a cached user but checkAuth is still in flight, show the page
  // immediately — it will update silently once /me responds.
  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Redirect to onboarding if not completed (unless we're already on the onboarding page)
  if (!allowOnboarding && !user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return showLayout ? <Layout>{children}</Layout> : children;
}

// App Router
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute showLayout={false} allowOnboarding={true}><Onboarding /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute><ImportText /></ProtectedRoute>} />
      <Route path="/texts" element={<ProtectedRoute><TextsList /></ProtectedRoute>} />
      <Route path="/texts/:textId" element={<ProtectedRoute showLayout={false}><TextViewer /></ProtectedRoute>} />
      <Route path="/reader/:textId" element={<ProtectedRoute><Reader /></ProtectedRoute>} />
      <Route path="/vocabulary" element={<ProtectedRoute><Vocabulary /></ProtectedRoute>} />
      <Route path="/review" element={<ProtectedRoute><Review /></ProtectedRoute>} />
      <Route path="/memory" element={<ProtectedRoute><MemoryDashboard /></ProtectedRoute>} />
      <Route path="/stats" element={<ProtectedRoute><Stats /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/language-priority" element={<ProtectedRoute><LanguagePriorityAssessment /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalSetter /></ProtectedRoute>} />
      <Route path="/game" element={<ProtectedRoute><Game /></ProtectedRoute>} />
      <Route path="/games" element={<ProtectedRoute><GamesHub /></ProtectedRoute>} />
      <Route path="/mailroom" element={<ProtectedRoute><MailRoom /></ProtectedRoute>} />
      <Route path="/bible-courses" element={<ProtectedRoute><BibleCourses /></ProtectedRoute>} />
      <Route path="/bible/:courseId/:bookId" element={<ProtectedRoute><BibleBook /></ProtectedRoute>} />
      <Route path="/bible/:courseId/:bookId/:chapter" element={<ProtectedRoute><BibleReader /></ProtectedRoute>} />
      <Route path="/grammar" element={<ProtectedRoute><GrammarTopics /></ProtectedRoute>} />
      <Route path="/grammar-library" element={<ProtectedRoute><GrammarLibrary /></ProtectedRoute>} />
      <Route path="/learning-path/:languageCode" element={<ProtectedRoute><LearningPath /></ProtectedRoute>} />
      <Route path="/alphabet-courses" element={<ProtectedRoute><AlphabetCourses /></ProtectedRoute>} />
      <Route path="/alphabet/:courseId" element={<ProtectedRoute><AlphabetLesson /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/community/forum/:categoryId" element={<ProtectedRoute><ForumCategory /></ProtectedRoute>} />
      <Route path="/community/forum/:categoryId/new" element={<ProtectedRoute><ForumNewPost /></ProtectedRoute>} />
      <Route path="/community/forum/post/:postId" element={<ProtectedRoute><ForumPost /></ProtectedRoute>} />
      <Route path="/community/chat/:roomId" element={<ProtectedRoute showLayout={false}><ChatRoom /></ProtectedRoute>} />
      <Route path="/community/chat/request" element={<ProtectedRoute><ChatRoomRequest /></ProtectedRoute>} />
      <Route path="/forge" element={<ProtectedRoute><ForgeLanguageSelection /></ProtectedRoute>} />
      <Route path="/forge/:languageCode" element={<ProtectedRoute><ForgePath /></ProtectedRoute>} />
      <Route path="/forge/:languageCode/lesson/:course/:lesson" element={<ProtectedRoute showLayout={false}><ForgeLesson /></ProtectedRoute>} />
      <Route path="/forge-stories" element={<ProtectedRoute><ForgeStoriesIndex /></ProtectedRoute>} />
      <Route path="/forge-stories/:languageCode" element={<ProtectedRoute><ForgeStories /></ProtectedRoute>} />
      <Route path="/forge-stories/:languageCode/read/:episode" element={<ProtectedRoute><ForgeStoryReader /></ProtectedRoute>} />
      <Route path="/listening/:languageCode" element={<ProtectedRoute><ListeningPage /></ProtectedRoute>} />
      <Route path="/conversation" element={<ProtectedRoute><ConversationPage /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/texts" element={<ProtectedRoute><AdminTexts /></ProtectedRoute>} />
      <Route path="/admin/entries" element={<ProtectedRoute><AdminEntries /></ProtectedRoute>} />
      <Route path="/admin/frequencies" element={<ProtectedRoute><AdminFrequencies /></ProtectedRoute>} />
      <Route path="/admin/sentences" element={<ProtectedRoute><AdminSentences /></ProtectedRoute>} />
      <Route path="/admin/videos" element={<ProtectedRoute><AdminFeaturedVideos /></ProtectedRoute>} />
      <Route path="/admin/grammar" element={<ProtectedRoute><AdminGrammarEditor /></ProtectedRoute>} />
      <Route path="/admin/entry-audit" element={<ProtectedRoute><AdminEntryAudit /></ProtectedRoute>} />
      <Route path="/admin/flashcards" element={<ProtectedRoute><AdminFlashcards /></ProtectedRoute>} />
      <Route path="/admin/flashcards/study/:listId" element={<ProtectedRoute><AdminFlashcardsStudy /></ProtectedRoute>} />
      <Route path="/admin/vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
      <Route path="/admin/logs" element={<ProtectedRoute><AdminLogs /></ProtectedRoute>} />
      <Route path="/admin/language-requests" element={<ProtectedRoute><AdminLanguageRequests /></ProtectedRoute>} />
      <Route path="/lab/sentences" element={<ProtectedRoute><SentenceExplorer /></ProtectedRoute>} />
      <Route path="/admin/batch-upload" element={<ProtectedRoute showLayout={false}><BatchUpload /></ProtectedRoute>} />
      <Route path="/buy-credits" element={<ProtectedRoute><BuyCredits /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/sentence-library" element={<ProtectedRoute><SentenceLibraryPage /></ProtectedRoute>} />
      <Route path="/verb-formations" element={<ProtectedRoute><VerbFormationLibrary /></ProtectedRoute>} />
      {/* Public — no auth redirect, used as iframe by iLing-FORGE extension */}
      <Route path="/oracle-embed" element={<OracleEmbed />} />
      <Route path="/series" element={<ProtectedRoute><SeriesList /></ProtectedRoute>} />
      <Route path="/series/:seriesId" element={<ProtectedRoute><SeriesDetail /></ProtectedRoute>} />
      <Route path="/videos" element={<ProtectedRoute><VideoLibrary /></ProtectedRoute>} />
      <Route path="/videos/:videoId" element={<ProtectedRoute><VideoReader /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute showLayout={false}><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute showLayout={false}><PaymentCancel /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Public route - redirects to dashboard if logged in
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function HalloweenLayer() {
  const { halloweenMode } = useTheme();
  return halloweenMode ? <HalloweenDecorations /> : null;
}

function ConditionalToaster() {
  const location = useLocation();
  if (location.pathname === '/oracle-embed') return null;
  return <Toaster position="top-right" richColors />;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <HalloweenLayer />
            <CommandPalette />
            <ReviewDuePopup />
            <ReviewPromptDialog />
            <FloatingStudyTimer />
            <ConditionalToaster />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
